/**
 * The basket, as a pure reducer.
 *
 * It lives apart from React because the two rules it enforces are product rules,
 * not rendering concerns, and they are worth testing without a DOM:
 *
 *  - One shop per order, in both storefront modes (PRD GST-032). Changing shop
 *    clears the basket, and the reducer will not do that implicitly: a change to
 *    a different shop with items in the basket is rejected, so the caller has to
 *    ask the guest first. A reducer that silently emptied the basket would make
 *    the confirmation dialog decorative.
 *  - Quantities are bounded per line by the catalogue's own cap
 *    (`maxQuantityPerOrder`), never by a number chosen in a component.
 *
 * The shelf it is handed is stated STRUCTURALLY (`BasketCatalogueItem`) rather
 * than imported, so nothing here depends on a wire type. The one import is
 * `formuleUnitPriceMinor`: what a set costs once its card has been answered is
 * arithmetic the sheet, the basket, the review step and the server all have to
 * agree on, and a copy of it here is the fourth screen disagreeing with the
 * checkout by exactly one dear juice (CAT-018). It is pure, so the reducer is.
 */

import { formuleUnitPriceMinor } from '@croissants/contracts/projections';

/** One answer to one bullet of a formule's menu card (CAT-018). */
export interface BasketChoice {
  readonly componentRank: number;
  readonly itemId: string;
  /**
   * The words the answer was chosen by, exactly as `BasketLine.name` is: what
   * the shelf called it in the guest's language at the moment they picked it.
   *
   * Never sent on the wire - the bag is packed from the composition, and this
   * is a label, not an input. It exists for the one screen that has to name a
   * part the shelf can no longer name: an option withdrawn overnight would
   * otherwise vanish from the recap, taking with it the only line that says
   * which part of the set went missing.
   */
  readonly name?: string;
}

export interface BasketLine {
  readonly itemId: string;
  readonly quantity: number;
  /**
   * The guest's answers to a formule's card, in bullet order. Absent on every
   * plain product. A LINE IS A PRODUCT AND ITS ANSWERS (founder, 2026-08-28):
   * two Montmartre with different juices are two lines carrying one `itemId`,
   * so everything that finds a line does it by `lineKey`, never by the id.
   */
  readonly choices?: readonly BasketChoice[];
  /**
   * The product name as it was shown to the guest when this line was added.
   *
   * The catalogue is the authority on what an item is called, but it is a
   * network call away and the guest is not: they chose "The Parisian", and the
   * app has no business forgetting that because a storefront request failed on
   * the review screen. GST-021 promises an itemised review of what is about to
   * be paid for, and an id is not an item.
   *
   * Optional because a basket rebuilt from a past order (GST-010) carries only
   * ids, and because baskets persisted before this field existed must still
   * restore. Never sent on the wire - the order is priced and named from the
   * catalogue server-side, and this is a label, not an input.
   */
  readonly name?: string;
}

export interface BasketState {
  /** Null in mode A, where the platform allocates the bakery (PRD GST-029). */
  readonly shopId: string | null;
  readonly lines: readonly BasketLine[];
}

export const EMPTY_BASKET: BasketState = { shopId: null, lines: [] };

/**
 * What makes a line THIS line: the product, and the answers given to it.
 *
 * Canonical - the answers are sorted by bullet before they are joined - so two
 * baskets that answered the same card the same way in a different order
 * collapse to one line rather than two. A plain product's key is its id, which
 * is what every caller that predates choices already used.
 */
export function lineKey(itemId: string, choices?: readonly BasketChoice[]): string {
  if (choices === undefined || choices.length === 0) return itemId;
  const answers = [...choices]
    .sort((a, b) => a.componentRank - b.componentRank)
    .map((choice) => `${choice.componentRank}=${choice.itemId}`)
    .join(',');
  return `${itemId}#${answers}`;
}

export function keyOf(line: BasketLine): string {
  return lineKey(line.itemId, line.choices);
}

/** Hard ceiling matching the wire contract (`BasketLine.quantity` max 99). */
export const MAX_LINE_QUANTITY = 99;

export type BasketAction =
  /** `name` is the catalogue label the guest tapped; it is remembered, not resolved later. */
  | {
      type: 'ADD';
      itemId: string;
      max: number | null;
      name?: string;
      choices?: readonly BasketChoice[];
    }
  | {
      type: 'SET_QUANTITY';
      itemId: string;
      quantity: number;
      max: number | null;
      name?: string;
      choices?: readonly BasketChoice[];
    }
  /**
   * Adds `quantity` ON TOP of the matching line, never replacing it. This is
   * what a formule's sheet commits: the guest answered the card and chose how
   * many, and the two formules already in the basket stay there (a
   * `SET_QUANTITY` from the sheet was overwriting them, 2026-08-29).
   */
  | {
      type: 'ADD_LINE';
      itemId: string;
      quantity: number;
      max: number | null;
      name?: string;
      choices?: readonly BasketChoice[];
    }
  | { type: 'REMOVE'; itemId: string; choices?: readonly BasketChoice[] }
  | { type: 'CLEAR' }
  /** Adopts a shop. Rejected when it would silently discard another shop's items. */
  | { type: 'SELECT_SHOP'; shopId: string | null }
  /** The guest confirmed the loss; clears and adopts in one step (PRD GST-032). */
  | { type: 'FORCE_SELECT_SHOP'; shopId: string | null }
  /** Rebuilds a basket from a past order (PRD GST-010). */
  | { type: 'REPLACE'; state: BasketState }
  /**
   * Drops several lines at once - the lines the catalogue serving this morning
   * no longer offers (see `unavailableLines`). It is one action rather than a
   * loop of `REMOVE` because the guest makes one decision, and because a loop
   * would persist and re-render the basket once per line.
   *
   * By LINE KEY, never by product id: a line is a product and its answers, so
   * dropping "the Montmartre" took the two beside it whose juices were fine.
   */
  | { type: 'REMOVE_LINES'; keys: readonly string[] }
  /**
   * Records the labels a loaded catalogue just gave us, so the basket keeps them
   * for the screens that may not get a catalogue of their own. A basket rebuilt
   * from a past order arrives with ids only; this is where it learns the names.
   */
  | { type: 'NAME_LINES'; names: Readonly<Record<string, string>> };

function clamp(quantity: number, max: number | null): number {
  const ceiling = max === null ? MAX_LINE_QUANTITY : Math.min(max, MAX_LINE_QUANTITY);
  return Math.max(0, Math.min(Math.trunc(quantity), ceiling));
}

/** A line with a usable label, or none at all - never an empty one. */
function lineWith(
  itemId: string,
  quantity: number,
  name: string | undefined,
  choices?: readonly BasketChoice[],
): BasketLine {
  const label = name?.trim();
  const named = label === undefined || label === '' ? {} : { name: label };
  const answered = choices === undefined || choices.length === 0 ? {} : { choices };
  return { itemId, quantity, ...named, ...answered };
}

function withQuantity(
  lines: readonly BasketLine[],
  itemId: string,
  quantity: number,
  name: string | undefined,
  choices: readonly BasketChoice[] | undefined,
): readonly BasketLine[] {
  const key = lineKey(itemId, choices);
  if (quantity <= 0) return lines.filter((entry) => keyOf(entry) !== key);
  const existing = lines.find((entry) => keyOf(entry) === key);
  if (existing === undefined) return [...lines, lineWith(itemId, quantity, name, choices)];
  // A quantity change must not erase a name the basket already knows: the
  // stepper on the review screen has no catalogue to hand when the storefront
  // call failed, which is exactly when the label matters.
  return lines.map((entry) =>
    keyOf(entry) === key
      ? lineWith(itemId, quantity, name ?? entry.name, entry.choices ?? choices)
      : entry,
  );
}

export function basketReducer(state: BasketState, action: BasketAction): BasketState {
  switch (action.type) {
    case 'ADD': {
      const current = quantityOf(state, action.itemId, action.choices);
      const next = clamp(current + 1, action.max);
      if (next === current) return state;
      return {
        ...state,
        lines: withQuantity(state.lines, action.itemId, next, action.name, action.choices),
      };
    }
    case 'ADD_LINE': {
      const current = quantityOf(state, action.itemId, action.choices);
      const next = clamp(current + Math.max(0, Math.trunc(action.quantity)), action.max);
      if (next === current) return state;
      return {
        ...state,
        lines: withQuantity(state.lines, action.itemId, next, action.name, action.choices),
      };
    }
    case 'SET_QUANTITY': {
      const next = clamp(action.quantity, action.max);
      if (next === quantityOf(state, action.itemId, action.choices)) return state;
      return {
        ...state,
        lines: withQuantity(state.lines, action.itemId, next, action.name, action.choices),
      };
    }
    case 'REMOVE': {
      const key = lineKey(action.itemId, action.choices);
      return { ...state, lines: state.lines.filter((line) => keyOf(line) !== key) };
    }
    case 'REMOVE_LINES': {
      const dropped = new Set(action.keys);
      if (dropped.size === 0) return state;
      const lines = state.lines.filter((line) => !dropped.has(keyOf(line)));
      return lines.length === state.lines.length ? state : { ...state, lines };
    }
    case 'CLEAR':
      return { ...state, lines: [] };
    case 'SELECT_SHOP': {
      if (state.shopId === action.shopId) return state;
      // Items belong to the shop that sold them; adopting another shop without an
      // explicit confirmation would lose them silently (PRD GST-032).
      if (state.lines.length > 0) return state;
      return { shopId: action.shopId, lines: [] };
    }
    case 'FORCE_SELECT_SHOP':
      return state.shopId === action.shopId ? state : { shopId: action.shopId, lines: [] };
    case 'REPLACE':
      return { shopId: action.state.shopId, lines: [...action.state.lines] };
    case 'NAME_LINES': {
      const named = state.lines.map((entry) =>
        lineWith(
          entry.itemId,
          entry.quantity,
          action.names[entry.itemId] ?? entry.name,
          entry.choices,
        ),
      );
      // Identity is the signal the provider persists on, so an unchanged basket
      // must come back as the very same object rather than an equal one.
      const changed = named.some((entry, index) => entry.name !== state.lines[index]?.name);
      return changed ? { ...state, lines: named } : state;
    }
    default:
      return state;
  }
}

/**
 * What to print for a basket line (PRD GST-021).
 *
 * Three tiers, in this order and for a reason:
 *  1. the loaded catalogue, which describes what will actually be delivered - a
 *     bakery that renamed an item between the tap and the review wins;
 *  2. the name captured when the guest added the line, which is what they chose;
 *  3. a translated placeholder, because a review that cannot name an item must
 *     say so in words rather than fall through to a catalogue id. The guest
 *     cannot check "1c56c0a6de71b12a" against what they wanted, so it is not a
 *     review - it is the screen before payment failing silently.
 */
export function lineLabel(
  line: BasketLine,
  catalogueName: string | undefined,
  placeholder: string,
): string {
  return nonEmpty(catalogueName) ?? nonEmpty(line.name) ?? placeholder;
}

function nonEmpty(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? undefined : trimmed;
}

/**
 * How many of ONE line - the product with these exact answers.
 *
 * A set whose card ASKS something and is given no `choices` is the un-answered
 * card, which is never in a basket, so the call from a tile that does not know
 * the answers reads zero; the tile's badge counts every line of the product
 * through `quantityOfProduct` instead. A set that asks nothing - every section
 * `INCLUDED` - has no answers to carry and sits under its plain product key,
 * so it reads here exactly as a croissant does.
 */
export function quantityOf(
  state: BasketState,
  itemId: string,
  choices?: readonly BasketChoice[],
): number {
  const key = lineKey(itemId, choices);
  return state.lines.find((line) => keyOf(line) === key)?.quantity ?? 0;
}

/**
 * The line of a product the guest added most recently - the one a tile's minus
 * takes one off when the product is a formule and the tile cannot know which
 * of its answered lines is meant.
 */
export function lastLineOf(state: BasketState, itemId: string): BasketLine | undefined {
  return state.lines.filter((line) => line.itemId === itemId).at(-1);
}

/** How many of a PRODUCT across every way it was answered - what a tile shows. */
export function quantityOfProduct(state: BasketState, itemId: string): number {
  return state.lines.reduce(
    (total, line) => (line.itemId === itemId ? total + line.quantity : total),
    0,
  );
}

export function itemCount(state: BasketState): number {
  return state.lines.reduce((total, line) => total + line.quantity, 0);
}

export function isEmpty(state: BasketState): boolean {
  return state.lines.length === 0;
}

/**
 * Every product the basket holds - each line's own and each part picked on a
 * set's card - once each, sorted: what the menu names to the server so the
 * shelf offers only what ONE counter can hand over with it (issue #1420).
 * Sorted, so the same basket always asks the same question and a reordering
 * costs no second read.
 */
export function heldItemIdsOf(state: BasketState): readonly string[] {
  const ids = new Set<string>();
  for (const line of state.lines) {
    ids.add(line.itemId);
    for (const choice of line.choices ?? []) ids.add(choice.itemId);
  }
  return [...ids].sort();
}

/**
 * True when adopting `shopId` would discard items - the caller must confirm with
 * the guest before dispatching `FORCE_SELECT_SHOP` (PRD GST-032).
 */
export function shopChangeLosesBasket(state: BasketState, shopId: string | null): boolean {
  return state.shopId !== shopId && state.lines.length > 0;
}

/**
 * The lines this morning's catalogue no longer offers.
 *
 * A basket outlives the morning it was built for. It is persisted per QR code and
 * nothing else, and in mode A the fulfilling bakery is chosen per morning
 * (GST-029), so a guest who fills a basket on Sunday evening and comes back on
 * Monday is holding item ids that belong to a catalogue nobody is serving today.
 * Left unnamed, those lines price at nothing, drag the subtotal to zero and are
 * rejected at quote time as an unservable address - a diagnosis about the flat,
 * for a problem in the basket, with no way forward.
 *
 * So it is a state, not an accident: the screens ask this question of the
 * catalogue they have actually loaded, say so on the line, and offer to drop it
 * (GST-003, GST-021, GST-026).
 *
 * A FORMULE line is its answers, so the item being on the shelf is only half
 * the question (CAT-018): this morning's card can ask something the line never
 * answered, or no longer offer what it answered with. The server refuses both
 * (`error.formule_answers`, UNANSWERED / NOT_OFFERED / NO_SUCH_BULLET) at the
 * quote, which is a screen with a red panel and nothing to press. Asked here
 * instead, they are lines the guest can drop.
 *
 * `itemOf` must come from a catalogue that ANSWERED. A storefront request that
 * failed knows nothing about what the bakery sells, and treating its silence as
 * "withdrawn" would tell a guest their basket is gone because their lift has no
 * signal.
 */
/**
 * The basket a PAST order rebuilds - what "Reorder same" dispatches (GST-010).
 *
 * ONE mapping, because there were two: the orders list and the order's own page
 * each turned the reply into `{ itemId, quantity }` by hand, and both dropped
 * the guest's ANSWERS. A line is a product AND its answers (founder,
 * 2026-08-28), so a set came back with its questions blank and `unavailableLines`
 * below - rightly - flagged the guest's own breakfast as one this morning does
 * not serve. Neither screen was wrong about anything else, which is exactly how
 * a defect written twice survives a review of either copy.
 *
 * It is `wireLinesOf` (lib/wire-lines.ts) read backwards, and the two were
 * written a day apart for the same reason: the quote and the checkout each
 * mapped a line by hand until one of them dropped the answers, and a guest was
 * told to "choose an option for every part of this formule" on a basket they
 * had just answered in full.
 *
 * Stated structurally rather than over the wire type, by the rule the rest of
 * this file follows: the basket names no contract TYPE, and the reply satisfies
 * this as it stands.
 */
export function basketFromPastOrder(basket: {
  readonly shopId: string | null;
  readonly lines: readonly {
    readonly itemId: string;
    readonly quantity: number;
    readonly choices?: readonly { readonly componentRank: number; readonly itemId: string }[];
  }[];
}): BasketState {
  return {
    shopId: basket.shopId,
    lines: basket.lines.map((line) => ({
      itemId: line.itemId,
      quantity: line.quantity,
      /* Absent on every plain product, so a rebuilt line keys exactly as the
         one the guest added by hand (`lineKey`). The choice carries no NAME:
         the wire has none, and the screens that need the words read them off
         the card they are ordering from (`formuleSubItems`). */
      ...(line.choices === undefined || line.choices.length === 0
        ? {}
        : {
            choices: line.choices.map((choice) => ({
              componentRank: choice.componentRank,
              itemId: choice.itemId,
            })),
          }),
    })),
  };
}

export function unavailableLines(
  state: BasketState,
  itemOf: (itemId: string) => BasketCatalogueItem | undefined,
): readonly BasketLine[] {
  return state.lines.filter((line) => {
    const item = itemOf(line.itemId);
    return item === undefined || answersStale(line, item.formule);
  });
}

/**
 * The shelf, as this file needs it: an item, its price, and the card it may
 * carry. Stated structurally rather than imported so the basket keeps no wire
 * type - `CatalogueItem` satisfies it as it stands.
 */
export interface BasketCatalogueItem {
  /**
   * What the item itself costs, VAT included, in minor units - for a set, the
   * card's price at its cheapest picks. Absent on a shelf entry that carries
   * no price, which is a line the subtotal cannot count.
   */
  readonly price?: number;
  readonly formule?: readonly {
    readonly rank: number;
    /** `'INCLUDED'` or `'CHOICE'` - only a `CHOICE` section is ever answered. */
    readonly kind: 'INCLUDED' | 'CHOICE';
    /** For a `CHOICE` section, how many of the picked option the set holds. */
    readonly quantity: number;
    readonly choices: readonly {
      readonly itemId: string;
      readonly quantity: number;
      /** What picking THIS option adds per unit of the pick (CAT-018). */
      readonly supplement?: number | undefined;
    }[];
  }[];
}

/**
 * True when this morning's card and this line's answers cannot both be right.
 *
 * A card the shelf does not carry says nothing either way - `formule` is absent
 * on a plain product AND on a formule whose composition the storefront could
 * not resolve, and reading the second as "withdrawn" would flag a line the
 * server would have priced perfectly well.
 */
function answersStale(line: BasketLine, card: BasketCatalogueItem['formule']): boolean {
  if (card === undefined || card.length === 0) return false;
  const answered = new Map((line.choices ?? []).map((choice) => [choice.componentRank, choice]));
  for (const [rank, choice] of answered) {
    const bullet = card.find((entry) => entry.rank === rank);
    if (bullet === undefined) return true;
    // An answer to a section the card no longer ASKS about is as stale as an
    // answer it no longer offers: the house turned a question into contents,
    // and the server prices the card rather than the answer.
    if (bullet.kind !== 'CHOICE') return true;
    if (!bullet.choices.some((option) => option.itemId === choice.itemId)) return true;
  }
  /*
   * A `CHOICE` section is a question however few options it holds this
   * morning, and a line that leaves one open is refused (CAT-018, founder
   * 2026-08-30). An `INCLUDED` section is never answered, however many items
   * it lists. The number of options decided this until the kind reached the
   * wire, and the shelf and the server read a narrowed section differently.
   */
  return card.some((bullet) => bullet.kind === 'CHOICE' && !answered.has(bullet.rank));
}

/**
 * Merchandise subtotal from the shelf, in integer minor units (PRD NFR-003).
 *
 * A line the catalogue cannot price contributes nothing, which is only honest
 * while the screen has also flagged it through `unavailableLines`: a total that
 * quietly omits an item the guest can still see is the failure GST-021 exists to
 * prevent. The authoritative total is the server quote either way (GST-022).
 *
 * It is handed the ITEM rather than a price because a set's line is not priced
 * by its card alone: the guest's answers may carry supplements, and the server
 * prices exactly what they picked (CAT-018). `formuleUnitPriceMinor` is that
 * one arithmetic; a plain product falls through it as its own price.
 */
export function subtotalMinorUnits(
  state: BasketState,
  itemOf: (itemId: string) => BasketCatalogueItem | undefined,
): number {
  return state.lines.reduce((total, line) => {
    const item = itemOf(line.itemId);
    if (item === undefined || item.price === undefined) return total;
    const unit = formuleUnitPriceMinor(item.price, item.formule ?? [], line.choices ?? []);
    return total + unit * line.quantity;
  }, 0);
}

/** Narrowing guard for a basket restored from storage. */
export function isBasketState(value: unknown): value is BasketState {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { shopId?: unknown; lines?: unknown };
  const shopOk = candidate.shopId === null || typeof candidate.shopId === 'string';
  if (!shopOk || !Array.isArray(candidate.lines)) return false;
  return candidate.lines.every((line: unknown) => {
    if (typeof line !== 'object' || line === null) return false;
    const entry = line as {
      itemId?: unknown;
      quantity?: unknown;
      name?: unknown;
      choices?: unknown;
    };
    const choicesOk =
      entry.choices === undefined ||
      (Array.isArray(entry.choices) &&
        entry.choices.every((choice: unknown) => {
          if (typeof choice !== 'object' || choice === null) return false;
          const answer = choice as { itemId?: unknown; componentRank?: unknown; name?: unknown };
          // The words it was chosen by are optional - every basket stored
          // before they were kept has none - but a present one has to be usable.
          const wordsOk =
            answer.name === undefined ||
            (typeof answer.name === 'string' && answer.name.trim() !== '');
          return (
            typeof answer.itemId === 'string' && Number.isInteger(answer.componentRank) && wordsOk
          );
        }));
    // A remembered label is optional - baskets stored before it existed, and
    // baskets rebuilt from a past order, legitimately have none - but a present
    // one has to be a usable string, never an empty or foreign value.
    const nameOk =
      entry.name === undefined || (typeof entry.name === 'string' && entry.name.trim() !== '');
    return (
      typeof entry.itemId === 'string' &&
      typeof entry.quantity === 'number' &&
      Number.isInteger(entry.quantity) &&
      entry.quantity > 0 &&
      nameOk &&
      choicesOk
    );
  });
}
